// components/entities/DealRenderer.tsx
import React, { useCallback } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { Handshake, DollarSign, Clock, Briefcase, User, Trash2, XCircle } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { BaseEntityProps, EntityRowAction, LeasingDealDTO } from '#types/types';
import { portalTableRow } from '../portal/portalTheme';
import { BrandRenderer } from './BrandRenderer';
import { UserSelector } from '../portal/UserSelector';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { dealShortCode } from '../portal/entityGraph';
const DEFAULT_ROW_COLUMN_ORDER = ['brand', 'code', 'unit', 'property', 'rent', 'status', 'projects', 'assignee', 'actions'] as const;

const translations = {
  en: {
    notAvailable: '—', markDead: 'Mark as Dead', deleteDeal: 'Delete Deal',
    deleteConfirm: 'Are you sure?', close: 'Close', view: 'View', financialTerms: 'Financial Terms',
    baseRent: 'Base Rent', opex: 'OPEX (CAM)', leaseTimeline: 'Lease Timeline',
    leaseStart: 'Lease Start', leaseEnd: 'Lease End', contactDetails: 'Contact Details',
    noContactName: 'No contact name', strategyNotes: 'Strategy & Notes',
    locale: 'en-US', dateLocale: 'en-GB', deal: 'Deal',
    draft: 'Draft', review: 'Review', approved: 'Approved', offerSent: 'Offer sent', signed: 'Signed', dead: 'Dead'
  },
  fr: {
    notAvailable: '—', markDead: 'Marquer mort', deleteDeal: 'Supprimer',
    deleteConfirm: 'Confirmer la suppression ?', close: 'Fermer', view: 'Voir', financialTerms: 'Finance',
    baseRent: 'Loyer base', opex: 'Charges', leaseTimeline: 'Calendrier',
    leaseStart: 'Début', leaseEnd: 'Fin', contactDetails: 'Contact',
    noContactName: 'Sans nom', strategyNotes: 'Stratégie',
    locale: 'fr-FR', dateLocale: 'fr-FR', deal: 'Deal',
    draft: 'Brouillon', review: 'En revue', approved: 'Approuve', offerSent: 'Offre envoyee', signed: 'Signe', dead: 'Abandonne'
  }
};

const statusColors: Record<string, string> = {
  SIGNED: 'bg-white/10 text-white border-white/20',
  REVIEW: 'bg-[#9A6DD7]/15 text-[#9A6DD7] border-[#9A6DD7]/20',
  APPROVED: 'bg-[#529CCA]/15 text-[#529CCA] border-[#529CCA]/20',
  OFFER_SENT: 'bg-[#FFA344]/15 text-[#FFA344] border-[#FFA344]/20',
  DEAD: 'bg-[#979A9B]/10 text-[#979A9B]/60 border-[#979A9B]/10',
  DRAFT: 'bg-[#979A9B]/10 text-[#979A9B] border-[#979A9B]/15'
};

const formatCurrency = (amount: any, currency: string | null | undefined, locale: string) => {
  const numeric = Number(amount);
  if (!numeric || !Number.isFinite(numeric)) return '—';
  return Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 0 }).format(numeric);
};

const formatDate = (value: string | null | undefined, locale: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
};

// Trois références figées : la cellule est rendue par colonne et par ligne, un littéral
// inline y recréerait un objet style à chaque rendu de chaque cellule.
const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };

const cellStyleFor = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === 'actions' || column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-2">
    <span className="text-xs text-white/40">{label}</span>
    <span className="text-sm text-white/90 font-medium">{value}</span>
  </div>
);

export interface DealRendererProps extends BaseEntityProps<LeasingDealDTO> {
  isExpanded?: boolean;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingDealDTO>[];
}

export const DealRenderer = React.memo((props: DealRendererProps) => {
  const { entityId, variant = 'card', onClick, className, isExpanded, columns, rowActions = [] } = props;
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];
  
  const deal = useKernelStore(state => state.inventory.Deal.registry[entityId]) as LeasingDealDTO | undefined;
  const brand = useKernelStore(state => deal?.brandId ? state.inventory.Brand.registry[deal.brandId] : null);
  const unit = useKernelStore(state => deal?.unitId ? state.inventory.Unit.registry[deal.unitId] : null);
  const property = useKernelStore(state => unit?.propertyId ? state.inventory.Property.registry[unit.propertyId] : null);
  const assignee = useKernelStore(state => deal?.assignedUserId ? state.inventory.User.registry[deal.assignedUserId] : null);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  }, [onClick]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    kernel.current?.setMenuData({
      position: { x: e.clientX, y: e.clientY },
      actions: [
        { label: t.markDead, icon: <XCircle size={14} />, onClick: () => kernel.current?.updateEntity('Deal', entityId, { status: 'DEAD' }) },
        { label: t.deleteDeal, icon: <Trash2 size={14} className="text-red-400" />, onClick: () => window.confirm(t.deleteConfirm) && kernel.current?.deleteEntity('Deal', entityId) }
      ]
    });
  }, [entityId, kernel, t]);

  const handleAssigneeSelect = useCallback((m: { id: string } | null) => {
    if (m) kernel.current?.updateEntity('Deal', entityId, { assignedUserId: m.id });
  }, [entityId, kernel]);

  if (!deal) return <EntitySkeleton variant={variant === 'page' ? 'detail' : variant} className={className} />;

  const statusLabels: Record<string, string> = {
    DRAFT: t.draft,
    REVIEW: t.review,
    APPROVED: t.approved,
    OFFER_SENT: t.offerSent,
    SIGNED: t.signed,
    DEAD: t.dead,
  };

  if (variant === 'row') {
    const code = dealShortCode(deal, {
      Brand: brand ? { [deal.brandId]: brand } : undefined,
      Unit: unit ? { [deal.unitId]: unit } : undefined,
      Property: property && unit?.propertyId ? { [unit.propertyId]: property } : undefined,
    });
    const cellRenderers: Record<string, React.ReactNode> = {
      brand: (
        <div className="h-full flex items-center gap-2 min-w-0">
          <BrandRenderer entityId={deal.brandId} variant="chip" className="!bg-transparent !border-none !p-0" />
          <span className="truncate text-sm text-white/90">
            {brand?.name || deal.tenantLegalName || t.notAvailable}
          </span>
        </div>
      ),
      code: (
        <div className="h-full flex items-center font-mono text-xs text-white/60 truncate">
          {code || t.notAvailable}
        </div>
      ),
      unit: (
        <div className="text-white/70 text-sm flex items-center h-full truncate">
          {unit?.unitIdentifier || t.notAvailable}
        </div>
      ),
      property: (
        <div className="text-white/80 text-sm flex items-center h-full truncate">
          {property?.name || t.notAvailable}
        </div>
      ),
      rent: (
        <div className="text-white/80 font-mono text-xs flex items-center h-full">
          {formatCurrency(deal.rentAmount, deal.rentCurrency, t.locale)}
        </div>
      ),
      status: (
        <div className="flex items-center h-full">
          <span className={clsx('px-2 py-0.5 rounded-full text-[9px] font-medium border', statusColors[deal.status] || statusColors.DRAFT)}>
            {statusLabels[deal.status] || t.draft}
          </span>
        </div>
      ),
      projects: (
        <div className="flex items-center h-full">
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 border border-white/5">
            <Briefcase size={12} className="text-white/30" />
            <span className="text-xs font-mono text-white/70">{deal.projectIds?.length || 0}</span>
          </div>
        </div>
      ),
      assignee: (
        <div className="flex items-center h-full">
          <UserSelector selectedMember={assignee as any} onSelect={handleAssigneeSelect} />
        </div>
      ),
      actions: (
        <div className="text-right flex items-center justify-end h-full text-xs text-white/50 gap-2">
          <EntityRowActions entityType="Deal" entity={deal} rowActions={rowActions} />
        </div>
      ),
    };

    const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));

    return (
      <div className="flex flex-col w-full">
        <div
          className={clsx(
            portalTableRow,
            styles.gridRow,
            styles.bodyRow,
            'group h-10 cursor-pointer border-b border-[#2A2A2A]',
            isExpanded ? 'bg-[#222222]' : 'bg-[#181818] hover:bg-[#1E1E1E]',
            className
          )}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          {activeColumns.map((column) => (
            <div
              key={column.id}
              className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={cellStyleFor(column)}
            >
              {cellRenderers[column.id] || null}
            </div>
          ))}
        </div>
        {isExpanded && (
          <div className="px-6 py-8 grid grid-cols-2 gap-12 border-t border-white/5" onClick={stopPropagation}>
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="text-[10px] text-white/30 flex items-center gap-2"><DollarSign size={12}/>{t.financialTerms}</div>
                <InfoRow label={t.baseRent} value={formatCurrency(deal.rentAmount, deal.rentCurrency, t.locale)} />
                <InfoRow label={t.opex} value={formatCurrency(deal.opexCamAmount, deal.rentCurrency, t.locale)} />
              </div>
              <div className="space-y-3">
                <div className="text-[10px] text-white/30 flex items-center gap-2"><User size={12}/>{t.contactDetails}</div>
                <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                  <div className="text-sm font-medium">{deal.contactName || t.noContactName}</div>
                  <div className="text-xs text-white/40">{deal.contactEmail}</div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="text-[10px] text-white/30 flex items-center gap-2"><Clock size={12}/>{t.leaseTimeline}</div>
                <InfoRow label={t.leaseStart} value={formatDate(deal.leaseStartDate, t.dateLocale)} />
                <InfoRow label={t.leaseEnd} value={formatDate(deal.leaseEndDate, t.dateLocale)} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={clsx("flex items-center gap-2 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs cursor-pointer hover:bg-white/10", className)} onClick={handleClick}>
      <Handshake size={12} className="text-white/40" />
      <span className="truncate">{brand?.name || t.deal} - {unit?.unitIdentifier || '...'}</span>
    </div>
  );
});
DealRenderer.displayName = 'DealRenderer';
